import type { WithId } from 'fitify-types/src/types/common'
import {
  type EmailCampaign,
  EmailCampaignStatus,
} from 'fitify-types/src/types/email-campaigns'
import { LoaderIcon } from 'fitify-ui/src/Icon/outline/LoaderIcon'
import { useEffect, useRef } from 'react'

import { CampaignToast } from 'components/Campaign/Toast/CampaignToast'
import { showToast, TOAST_AUTO_CLOSE } from 'utils/toast'

type Props = {
  campaigns: WithId<EmailCampaign>[]
}

export const useCampaignsToast = ({ campaigns }: Props) => {
  const previousStatusesRef = useRef<Map<string, EmailCampaignStatus>>(
    new Map()
  )

  useEffect(() => {
    const previousStatuses = previousStatusesRef.current
    const currentStatuses = new Map<string, EmailCampaignStatus>()

    campaigns.forEach(({ campaign_code, status, status_message }) => {
      currentStatuses.set(campaign_code, status)

      const previousStatus = previousStatuses.get(campaign_code)

      if (
        status === EmailCampaignStatus.CREATED ||
        status === EmailCampaignStatus.AUDIENCE_CREATED
      ) {
        showToast(
          <CampaignToast
            campaignCode={campaign_code}
            message={status_message || 'Campaign is being processed'}
          />,
          {
            type: 'info',
            toastId: campaign_code,
            autoClose: false,
            icon: <LoaderIcon color="White" />,
          }
        )
      } else if (status === EmailCampaignStatus.ERROR) {
        showToast(
          <CampaignToast
            campaignCode={campaign_code}
            message={status_message || 'Failed to process campaign'}
          />,
          {
            type: 'error',
            toastId: campaign_code,
            autoClose: false,
          }
        )
      } else if (
        status === EmailCampaignStatus.SCHEDULED &&
        previousStatus &&
        previousStatus !== EmailCampaignStatus.SCHEDULED
      ) {
        showToast(
          <CampaignToast
            campaignCode={campaign_code}
            message={status_message || 'Campaign has been scheduled!'}
          />,
          {
            type: 'success',
            toastId: campaign_code,
            autoClose: TOAST_AUTO_CLOSE,
          }
        )
      }
    })

    previousStatusesRef.current = currentStatuses
  }, [campaigns])
}
